import React, { useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { prop } from 'ramda';
import { useNavigate } from 'js/hooks/navigate';
import { Icon } from 'js/components/common/Icon';
import { isExternalUrl, openWindow, redirect } from 'js/utils/url.util';
import { useUserSelector, useDeviceSelector, useMyAccountSidebarSelector } from 'js/store/hooks';

import * as helpers from './helpers';
import * as hooks from '../../../../hooks';
import * as actions from '../../../../store/actions';

import style from './Menu.scss';

export const Menu = () => {
    const subMenuRef = useRef();
    const mainMenuRef = useRef();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const menuFocusableRef = useRef();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const formFactor = useDeviceSelector(prop('formFactor'));
    const closeSidebar = hooks.useHandleSidebarClose();
    const {
        height,
        subMenuItems,
        mainMenuItems,
        isSubMenuShown,
        transitionDuration
    } = useMyAccountSidebarSelector(prop('sidebarMenu'));

    const handleMenuItemClick = useCallback((event, item) => {
        event.preventDefault();

        if (item.children?.length) {
            dispatch(actions.showSubMenu(item.children));

            return;
        }

        if (item.openIn === 'default') {
            if (isExternalUrl(item.url)) {
                openWindow(item.url);
            } else {
                dispatch(actions.showPortalPage({ pageURL: item.url, isBackArrowHidden: false }));
            }
        }

        if (item.openIn === 'internal') {
            if (isExternalUrl(item.url)) {
                redirect(item.url);
            } else {
                navigate(item.url);
                closeSidebar();
            }
        }

        if (item.openIn === 'external') {
            openWindow(item.url);
        }
    }, [dispatch, navigate, closeSidebar]);

    const focusOnContainer = useCallback(() => {
        menuFocusableRef.current.focus();
    }, []);

    const mainMenuItemsMemo = useMemo(() =>
        helpers.filterMenuItems({ menuItems: mainMenuItems, isLoggedIn, formFactor }),
    [mainMenuItems, isLoggedIn, formFactor]);

    const isMainMenuHasIcons = useMemo(() =>
        mainMenuItemsMemo && mainMenuItemsMemo.some((menuItem) => Boolean(menuItem.iconUrl)),
    [mainMenuItemsMemo]);

    const subMenuItemsMemo = useMemo(() =>
        helpers.filterMenuItems({ menuItems: subMenuItems, isLoggedIn, formFactor }),
    [subMenuItems, isLoggedIn, formFactor]);

    const isSubMenuHasIcons = useMemo(() =>
        subMenuItemsMemo && subMenuItemsMemo.some((menuItem) => Boolean(menuItem.iconUrl)),
    [subMenuItemsMemo]);

    useEffect(() => {
        setImmediate(() => {
            if (!mainMenuRef.current || !subMenuRef.current) {
                return;
            }

            const { height: mainMenuHeight } = window.getComputedStyle(mainMenuRef.current);
            const { height: subMenuHeight } = window.getComputedStyle(subMenuRef.current);

            dispatch(actions.changeMenuHeight(isSubMenuShown ? subMenuHeight : mainMenuHeight));
        });
    }, [dispatch, isSubMenuShown, isLoggedIn]);

    return (
        <>
            <span tabIndex={-1} ref={menuFocusableRef} aria-hidden={true} />
            <div
                style={{ height }}
                className={style.menuContainer}
            >
                {Boolean(mainMenuItemsMemo?.length) && (
                    <div
                        ref={mainMenuRef}
                        style={{ transitionDuration }}
                        onTransitionEnd={focusOnContainer}
                        className={`${style.mainMenuList} ${isSubMenuShown ? style.left : ''}`}
                    >
                        {mainMenuItemsMemo.map((item) => (
                            <a
                                href="#"
                                role="menuitem"
                                key={item.id}
                                className={style.menuListItem}
                                tabIndex={isSubMenuShown ? -1 : undefined}
                                onClick={(event) => handleMenuItemClick(event, item) }
                            >
                                {isMainMenuHasIcons && <span className={style.icon} style={{ backgroundImage: item.iconUrl && `url("${item.iconUrl}")` }}></span>}
                                <span className={style.name}>{item.name}</span>
                                {Boolean(item.children?.length) && <Icon className={style.rightArrow} type="rightListArrow" />}
                            </a>
                        ))}
                    </div>
                )}

                <div
                    ref={subMenuRef}
                    style={{ transitionDuration }}
                    className={`${style.subMenuList} ${isSubMenuShown ? style.center : ''}`}
                >
                    {subMenuItemsMemo?.map((item) => (
                        <a
                            href='#'
                            role="menuitem"
                            key={item.id}
                            className={style.menuListItem}
                            tabIndex={isSubMenuShown ? undefined : -1}
                            onClick={(event) => handleMenuItemClick(event, item) }
                        >
                            {isSubMenuHasIcons && <span className={style.icon} style={{ backgroundImage: item.iconUrl && `url("${item.iconUrl}")` }} />}
                            <span className={style.name}>{item.name}</span>
                        </a>
                    ))}
                </div>
            </div>
        </>
    );
};
